
import React from "react";
import { PageContainer } from "@/components/layout/PageContainer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { motion } from "framer-motion";

const FAQPage: React.FC = () => {
  const faqs = [
    {
      question: "How does PokeScan determine the value of my card?",
      answer: "PokeScan uses advanced computer vision and AI technology to identify the exact card in your collection. Once identified, we cross-reference with real-time market data from various sources including auction sites, online marketplaces, and card trading platforms to provide you with the most accurate current value."
    },
    {
      question: "How accurate is the card scanner?",
      answer: "Our scanner has a high accuracy rate for cards in good condition with clear visibility. For best results, scan your card in good lighting, place it on a dark, non-reflective surface, and ensure the entire card is visible in the frame. The scanner works best with English language cards from official sets released in North America, Europe, and Japan."
    },
    {
      question: "What factors affect the value of my Pokémon cards?",
      answer: "Multiple factors affect card values including: rarity (common, uncommon, rare, ultra-rare), condition (mint, near-mint, played), special features (holographic, full art, alternate art), age (vintage cards are often more valuable), popularity of the Pokémon featured, and current market demand."
    },
    {
      question: "Can I scan cards from any Pokémon TCG set?",
      answer: "PokeScan supports all official Pokémon Trading Card Game sets from Base Set through the current releases. This includes standard sets, special collections, promos, and Japanese exclusives. We continuously update our database to include new releases as they become available."
    },
    {
      question: "How do I add cards to my collection?",
      answer: "After scanning a card, you'll see a 'Add to Collection' button on the results page. Click this button to save the card to your digital collection. You can also manually add cards by searching in the collection section and choosing the specific edition of the card you own."
    },
    {
      question: "Is there a limit to how many cards I can scan?",
      answer: "Free accounts can scan up to 5 cards per day. Premium subscribers enjoy unlimited card scans and additional features like collection analytics, price alerts, and export capabilities."
    },
    {
      question: "How often are card values updated?",
      answer: "Our market data is updated daily to ensure you have the most current valuation information. For highly volatile cards (particularly rare cards that are seeing significant market movement), we may update prices multiple times per day."
    },
    {
      question: "Can PokeScan detect fake or counterfeit cards?",
      answer: "While our system can identify many common counterfeits based on printing anomalies, color discrepancies, and incorrect font usage, it's not infallible. If you're purchasing valuable cards, we recommend additional authentication methods or professional grading services."
    },
    {
      question: "Does PokeScan work with graded cards (PSA, BGS, CGC)?",
      answer: "Yes! PokeScan can identify cards that have been professionally graded and are in slabs. When scanning graded cards, ensure there's minimal glare on the slab and that the card is clearly visible through the case. The system will also factor in the grade when providing valuation information."
    },
    {
      question: "What information does PokeScan store about my collection?",
      answer: "We store the card data (set, number, name), condition information you provide, when it was added to your collection, any custom notes you add, and the market value at the time of scanning. We take privacy seriously and don't share your collection information with third parties."
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delay: 0.3,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <PageContainer>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-3xl mx-auto"
      >
        <h1 className="text-3xl font-bold mb-2">Frequently Asked Questions</h1>
        <p className="text-lg text-muted-foreground mb-8">
          Find answers to the most common questions about PokeScan and Pokémon card collecting.
        </p>
        
        <Card>
          <CardHeader className="bg-gradient-to-r from-pokemon-blue/10 to-transparent">
            <CardTitle>Common Questions</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <motion.div key={index} variants={itemVariants}>
                    <AccordionItem value={`item-${index}`} className="border-b border-gray-200">
                      <AccordionTrigger className="text-left font-medium hover:text-pokemon-blue">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-600">
                        <div className="pt-2 pb-4">
                          {faq.answer}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </motion.div>
                ))}
              </Accordion>
            </motion.div>
          </CardContent>
        </Card>
        
        <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-100">
          <h3 className="text-lg font-medium text-blue-800 mb-2">Still have questions?</h3>
          <p className="text-sm text-blue-600 mb-4">
            We're here to help! Contact our support team and we'll get back to you as soon as possible.
          </p>
          <div className="flex items-center justify-between">
            <span className="text-sm text-blue-600">+47 481 05 946</span>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm">
              Contact Support
            </button>
          </div>
        </div>
      </motion.div>
    </PageContainer>
  );
};

export default FAQPage;
